import React, { useState } from 'react';
import { Modal, Form, InputNumber, Select, Input, message, Space } from 'antd';
import CategoryService from '@/services/category.service';
import { logger } from '@/utils/logger';

const { Option } = Select;
const { TextArea } = Input;

interface CreateCategoryModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

export const CreateCategoryModal: React.FC<CreateCategoryModalProps> = ({
  visible,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      setLoading(true);

      // Проверяем существование категории
      const checkResponse = await CategoryService.checkExists({
        weightFrom: values.weightFrom,
        weightTo: values.weightTo,
        ageFrom: values.ageFrom,
        ageTo: values.ageTo,
        gender: values.gender,
        beltLevels: []
      });

      if (checkResponse.data?.exists && checkResponse.data?.data) {
        message.info('Категория с такими параметрами уже существует');
        form.resetFields();
        onSuccess();
        return;
      }

      // Создаем категорию
      const response = await CategoryService.create({
        name: values.name || generateCategoryName(values),
        description: values.description,
        weightFrom: values.weightFrom,
        weightTo: values.weightTo,
        ageFrom: values.ageFrom,
        ageTo: values.ageTo,
        gender: values.gender
      });

      if (response) {
        message.success('Категория успешно создана');
        form.resetFields();
        onSuccess();
      }
    } catch (error: any) {
      logger.error('Error creating category:', error);
      message.error(error?.response?.data?.error || 'Ошибка при создании категории');
    } finally {
      setLoading(false);
    }
  };

  const generateCategoryName = (values: any): string => {
    const genderMap: Record<string, string> = {
      'male': 'Мужчины',
      'female': 'Женщины',
      'mixed': 'Смешанная'
    };
    
    const gender = genderMap[values.gender] || values.gender;
    const age = `${values.ageFrom}-${values.ageTo} лет`;
    const weight = `${values.weightFrom}-${values.weightTo} кг`;
    
    return `${gender}, ${age}, ${weight}`;
  };


  return (
    <Modal
      title="Создать новую категорию"
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText="Создать"
      cancelText="Отмена"
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          gender: 'male',
          weightFrom: 0,
          weightTo: 100,
          ageFrom: 0,
          ageTo: 100
        }}
      >
        <Form.Item
          label="Название (необязательно, будет сгенерировано автоматически)"
          name="name"
        >
          <Input placeholder="Например: Дети (мальчики) 6-7 лет" />
        </Form.Item>

        <Form.Item
          label="Описание (необязательно)"
          name="description"
        >
          <TextArea rows={2} placeholder="Описание категории" />
        </Form.Item>

        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Form.Item
            label="Пол"
            name="gender"
            rules={[{ required: true, message: 'Выберите пол' }]}
          >
            <Select>
              <Option value="male">Мужчины</Option>
              <Option value="female">Женщины</Option>
              <Option value="mixed">Смешанная</Option>
            </Select>
          </Form.Item>

          <Space>
            <Form.Item
              label="Возраст от"
              name="ageFrom"
              rules={[{ required: true, message: 'Введите минимальный возраст' }]}
            >
              <InputNumber min={0} max={100} style={{ width: 120 }} />
            </Form.Item>

            <Form.Item
              label="Возраст до"
              name="ageTo"
              rules={[{ required: true, message: 'Введите максимальный возраст' }]}
            >
              <InputNumber min={0} max={100} style={{ width: 120 }} />
            </Form.Item>
          </Space>

          <Space>
            <Form.Item
              label="Вес от (кг)"
              name="weightFrom"
              rules={[{ required: true, message: 'Введите минимальный вес' }]}
            >
              <InputNumber min={0} max={200} step={0.1} precision={1} style={{ width: 120 }} />
            </Form.Item>

            <Form.Item
              label="Вес до (кг)"
              name="weightTo"
              rules={[{ required: true, message: 'Введите максимальный вес' }]}
            >
              <InputNumber min={0} max={200} step={0.1} precision={1} style={{ width: 120 }} />
            </Form.Item>
          </Space>

        </Space>
      </Form>
    </Modal>
  );
};

export default CreateCategoryModal;
